/**
 * Cart Optimizer — TCGPlayer-style cart consolidation.
 * Pure functions, no side effects.
 */
import constants from '../config';

var CONDITION_RANK = { 'NM': 5, 'LP': 4, 'MP': 3, 'HP': 2, 'DMG': 1, 'Damaged': 1 };

var FREE_SHIPPING_THRESHOLD = constants.FREE_SHIPPING_THRESHOLD || 10;
var FLAT_SHIPPING_FEE = constants.FLAT_SHIPPING_FEE || 1.00;
var TRANSACTION_FEE = constants.TRANSACTION_FEE || 0.50;

function getConditionRank(cond) {
  if (!cond) return 5; // default NM
  return CONDITION_RANK[cond] || 5;
}

/**
 * Partition cart into locked (share refs / no cardId) and optimizable items.
 */
function partitionCart(cart, loadShareRefFn) {
  var locked = [];
  var optimizable = [];
  cart.forEach(function(item) {
    if (!item.cardId) {
      locked.push(item);
    } else if (loadShareRefFn && loadShareRefFn(item.postId)) {
      locked.push(item);
    } else {
      optimizable.push(item);
    }
  });
  return { locked: locked, optimizable: optimizable };
}

/**
 * Calculate total cost of a cart (subtotal + shipping + tx fees).
 */
function calculateTotalCost(items) {
  var sellerSubtotals = {};
  items.forEach(function(item) {
    var key = item.sellerId || 'unknown';
    if (!sellerSubtotals[key]) sellerSubtotals[key] = 0;
    sellerSubtotals[key] += (Number(item.price) || 0) * (item.quantity || 1);
  });

  var subtotal = 0;
  var shippingTotal = 0;
  var numSellers = Object.keys(sellerSubtotals).length;

  Object.keys(sellerSubtotals).forEach(function(key) {
    subtotal += sellerSubtotals[key];
    if (sellerSubtotals[key] < FREE_SHIPPING_THRESHOLD) {
      shippingTotal += FLAT_SHIPPING_FEE;
    }
  });

  return subtotal + shippingTotal + (TRANSACTION_FEE * numSellers);
}

/**
 * Find the best alternative for an item from a specific seller.
 * Returns null if no valid alternative exists.
 */
function findAltFromSeller(item, sellerId, alternatives) {
  var key = item.cardId + '__' + (item.foil ? 'foil' : 'nonfoil');
  var alts = alternatives[key];
  if (!alts || alts.length === 0) return null;

  var itemRank = getConditionRank(item.condition);
  var neededQty = item.quantity || 1;
  var bestAlt = null;

  for (var i = 0; i < alts.length; i++) {
    var alt = alts[i];
    if (alt.sellerId !== sellerId) continue;
    var altRank = getConditionRank(alt.condition);
    if (altRank < itemRank) continue; // no downgrades
    if (alt.price > item.price * 1.15) continue; // within 15% price tolerance
    var altQty = alt.quantity || 1;
    if (altQty < neededQty) continue; // not enough stock
    if (!bestAlt || alt.price < bestAlt.price) {
      bestAlt = alt;
    }
  }
  return bestAlt;
}

/**
 * Build a swap record from an item and its alternative.
 */
function buildSwap(item, alt) {
  var conditionChange = null;
  if (alt.condition && alt.condition !== (item.condition || 'NM')) {
    conditionChange = { from: item.condition || 'NM', to: alt.condition };
  }
  return {
    from: {
      postId: item.postId,
      title: item.title,
      price: item.price,
      sellerId: item.sellerId,
      sellerName: item.sellerName,
      sellerRating: item.sellerRating || 0,
      condition: item.condition || 'NM',
    },
    to: {
      postId: alt.postId,
      title: alt.title,
      price: alt.price,
      sellerId: alt.sellerId,
      sellerName: alt.sellerName,
      sellerRating: alt.sellerRating || 0,
      condition: alt.condition || 'NM',
    },
    conditionChange: conditionChange,
  };
}

/**
 * Apply an alternative swap to an item, preserving cart metadata.
 */
function applySwap(item, alt) {
  var altQty = alt.quantity || 1;
  var itemQty = item.quantity || 1;
  return Object.assign({}, item, {
    postId: alt.postId,
    title: alt.title,
    price: alt.price,
    sellerId: alt.sellerId,
    sellerName: alt.sellerName,
    image: alt.cardImage || item.image,
    cardImage: alt.cardImage || item.cardImage,
    condition: alt.condition || item.condition,
    shippingCost: alt.shippingCost || 0,
    availableQuantity: altQty,
    quantity: Math.min(itemQty, altQty),
  });
}

/**
 * Run greedy optimization to consolidate toward fewer sellers.
 *
 * Strategy:
 * 1. Build a seller coverage map: for each seller in alternatives, which items can they cover?
 * 2. Score each candidate seller by how many NEW items they could absorb from other sellers
 * 3. Greedily pick the best consolidation target and swap items to it
 * 4. Repeat until no more beneficial consolidations exist
 */
function optimizeCart(locked, optimizable, alternatives) {
  if (optimizable.length === 0) return { newCart: locked.slice(), swaps: [] };

  // Work with copies so we can mutate
  var items = optimizable.map(function(item) { return Object.assign({}, item); });
  var swaps = [];

  // Track original seller count to ensure we never increase it
  var origCartSellers = {};
  locked.forEach(function(item) {
    if (item.sellerId) origCartSellers[item.sellerId] = true;
  });
  items.forEach(function(item) {
    if (item.sellerId) origCartSellers[item.sellerId] = true;
  });
  var origSellerCount = Object.keys(origCartSellers).length;

  // Build coverage: for each seller in alternatives, which item indices can they serve?
  var allAltSellers = {};
  for (var i = 0; i < items.length; i++) {
    var key = items[i].cardId + '__' + (items[i].foil ? 'foil' : 'nonfoil');
    var alts = alternatives[key];
    if (!alts) continue;
    for (var j = 0; j < alts.length; j++) {
      var alt = alts[j];
      if (!alt.sellerId) continue;
      if (!allAltSellers[alt.sellerId]) allAltSellers[alt.sellerId] = {};
      allAltSellers[alt.sellerId][i] = true;
    }
  }

  // Also register current sellers as covering their own items
  for (var i = 0; i < items.length; i++) {
    if (!allAltSellers[items[i].sellerId]) allAltSellers[items[i].sellerId] = {};
    allAltSellers[items[i].sellerId][i] = true;
  }

  // Greedy consolidation loop
  var maxIterations = 10;
  for (var iter = 0; iter < maxIterations; iter++) {
    // Count items per seller in current state
    var currentSellerItems = {};
    for (var i = 0; i < items.length; i++) {
      var s = items[i].sellerId;
      if (!currentSellerItems[s]) currentSellerItems[s] = [];
      currentSellerItems[s].push(i);
    }

    var bestTarget = null;
    var bestMoveIndices = null;
    var bestSavings = 0;

    // For each seller that has alternatives, evaluate consolidating items to them
    var sellerIds = Object.keys(allAltSellers);
    for (var si = 0; si < sellerIds.length; si++) {
      var targetSellerId = sellerIds[si];
      var coveredIndices = allAltSellers[targetSellerId];

      // Find items NOT already from this seller that could be moved to this seller
      var moveIndices = [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].sellerId === targetSellerId) continue; // already from this seller
        if (!coveredIndices[i]) continue; // no alternative from this seller
        // Check if a valid alternative actually exists (condition + price check)
        var alt = findAltFromSeller(items[i], targetSellerId, alternatives);
        if (alt) moveIndices.push(i);
      }

      if (moveIndices.length === 0) continue;

      // Simulate the swap and calculate savings
      var simItems = items.map(function(item, idx) {
        if (moveIndices.indexOf(idx) === -1) return item;
        var alt = findAltFromSeller(item, targetSellerId, alternatives);
        return alt ? applySwap(item, alt) : item;
      });

      var simCart = locked.concat(simItems);
      var origCart = locked.concat(items);
      var simCost = calculateTotalCost(simCart);
      var origCost = calculateTotalCost(origCart);
      var savings = origCost - simCost;

      // Guard: never increase the number of sellers beyond original count
      var simSellers = {};
      simCart.forEach(function(it) { if (it.sellerId) simSellers[it.sellerId] = true; });
      if (Object.keys(simSellers).length > origSellerCount) continue;

      if (savings > bestSavings) {
        bestSavings = savings;
        bestTarget = targetSellerId;
        bestMoveIndices = moveIndices;
      }
    }

    // No beneficial consolidation found, stop
    if (!bestTarget || bestSavings <= 0) break;

    // Apply the best consolidation
    for (var mi = 0; mi < bestMoveIndices.length; mi++) {
      var idx = bestMoveIndices[mi];
      var alt = findAltFromSeller(items[idx], bestTarget, alternatives);
      if (alt) {
        swaps.push(buildSwap(items[idx], alt));
        items[idx] = applySwap(items[idx], alt);
      }
    }
  }

  return { newCart: locked.concat(items), swaps: swaps };
}

export { partitionCart, calculateTotalCost, optimizeCart, CONDITION_RANK };
